import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService, type PriceLinkageResponse, type PriceLinkageSessionType } from '../services/api';

type CacheEntry<T> = {
  data: T;
  ts: number;
};

const CACHE_TTL_MS = 2 * 60 * 1000;
const dataCache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

const isNoDataError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  return (
    message.includes('404') ||
    message.includes('not found') ||
    message.includes('not enough data') ||
    message.includes('no data')
  );
};

const getCache = <T,>(key: string): CacheEntry<T> | null => {
  const cached = dataCache.get(key) as CacheEntry<T> | undefined;
  if (!cached) return null;
  if (Date.now() - cached.ts > CACHE_TTL_MS) return null;
  return cached;
};

const setCache = <T,>(key: string, data: T): void => {
  dataCache.set(key, { data, ts: Date.now() });
};

const fetchWithDedupe = async <T,>(key: string, fetcher: () => Promise<T>): Promise<T> => {
  const cachedPromise = inFlight.get(key) as Promise<T> | undefined;
  if (cachedPromise) return cachedPromise;
  const request = fetcher().finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, request as Promise<unknown>);
  return request;
};

type PriceLinkageHookResult = {
  data: PriceLinkageResponse | null;
  isLoading: boolean;
  error: string | null;
  isEmpty: boolean;
  refresh: () => Promise<void>;
};

type TopPriceLinkageHookResult = {
  data: PriceLinkageResponse[];
  isLoading: boolean;
  error: string | null;
  isEmpty: boolean;
  refresh: () => Promise<void>;
};

export function usePriceLinkage(
  ticker?: string,
  sessionType: PriceLinkageSessionType | string = 'REGULAR',
  windowDays: number = 60
): PriceLinkageHookResult {
  const normalizedTicker = (ticker || '').trim().toUpperCase();
  const key = useMemo(
    () => `price-linkage:${normalizedTicker}:${sessionType}:${windowDays}`,
    [normalizedTicker, sessionType, windowDays]
  );

  const [data, setData] = useState<PriceLinkageResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  const runFetch = useCallback(async () => {
    if (!normalizedTicker) {
      setData(null);
      setError(null);
      setIsEmpty(false);
      setIsLoading(false);
      return;
    }

    const cached = getCache<PriceLinkageResponse | null>(key);
    if (cached) {
      setData(cached.data);
      setIsEmpty(cached.data === null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithDedupe(key, () =>
        apiService.getPriceLinkage(normalizedTicker, sessionType, windowDays)
      );
      setCache(key, response);
      setData(response);
      setIsEmpty(false);
    } catch (err) {
      if (isNoDataError(err)) {
        setCache(key, null);
        setData(null);
        setIsEmpty(true);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load price linkage.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [key, normalizedTicker, sessionType, windowDays]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  const refresh = useCallback(async () => {
    dataCache.delete(key);
    await runFetch();
  }, [key, runFetch]);

  return { data, isLoading, error, isEmpty, refresh };
}

export function useTopPriceLinkage(
  limit: number = 10,
  sessionType: PriceLinkageSessionType | string = 'REGULAR',
  windowDays: number = 60
): TopPriceLinkageHookResult {
  const key = useMemo(
    () => `price-linkage-top:${limit}:${sessionType}:${windowDays}`,
    [limit, sessionType, windowDays]
  );

  const [data, setData] = useState<PriceLinkageResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  const runFetch = useCallback(async () => {
    const cached = getCache<PriceLinkageResponse[]>(key);
    if (cached) {
      setData(cached.data);
      setIsEmpty(cached.data.length === 0);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithDedupe(key, () =>
        apiService.getTopPriceLinkage(limit, sessionType, windowDays)
      );
      const rows = Array.isArray(response.items) ? response.items : [];
      const sorted = [...rows].sort((a, b) => (b.raw?.qualityScore ?? 0) - (a.raw?.qualityScore ?? 0));
      setCache(key, sorted);
      setData(sorted);
      setIsEmpty(sorted.length === 0);
    } catch (err) {
      if (isNoDataError(err)) {
        setCache(key, []);
        setData([]);
        setIsEmpty(true);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load top price linkage.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [key, limit, sessionType, windowDays]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  const refresh = useCallback(async () => {
    dataCache.delete(key);
    await runFetch();
  }, [key, runFetch]);

  return { data, isLoading, error, isEmpty, refresh };
}
